import { useState } from 'react';
import type { Candidate, MatchBasisItem } from '@ni/shared';
import { Checkbox } from '../ui/checkbox';
import { WorkingButton } from '../ui/working-button';
import {
  SAME_PERSON_OTHER,
  SAME_PERSON_THIS,
  matchKindWords,
  matchesRecordLine,
} from '../../lib/copy';
import './candidates.css';

/**
 * **One candidate card, for both lists that can bind one** (extracted from
 * `pick-list.tsx`, 2026-09-03).
 *
 * The pick list renders it and so does the `over_five` held list, because the
 * two are the same act: the office says "this is him", the near-match questions
 * it ticked ride along as `anchor_corrections`, and the server runs one binding
 * path on either. A held row that showed only a name and a detail line would be
 * asking the office to identify somebody with less than the pick list gives
 * them — and would have no way to answer "Matched \u2018Heritage\u2019. Treat as
 * Meritage Investment Group?" at all.
 *
 * Only the button's words differ, which is why they are props. On the pick
 * list's same-person mode (2026-10-12) the button gives way to a checkbox, and
 * the card reports its near-match answers with every change so the list can
 * send them with the whole set.
 */

type Corrections = { original: string; corrected: string }[];

/** The near-match answers a card sends when the office has ticked nothing: every one, accepted. */
export function defaultCorrections(candidate: Candidate): Corrections {
  return candidate.match_basis
    .map((item) => item.near_match)
    .filter((near): near is { original: string; suggested: string } => Boolean(near))
    .map((near) => ({ original: near.original, corrected: near.suggested }));
}

/**
 * Every candidate shares the subject's name, so a name row tells the office
 * nothing a card's heading does not. What tells their person from a namesake is
 * a detail on their own record that a source agreed with: each one is a plain
 * line, with the source's words under it (Justin Wade, 2026-10-06: the line
 * that would have picked him out sat behind a toggle labelled with an anchor
 * kind).
 */
const NAME_ANCHORS: ReadonlySet<string> = new Set(['name', 'aka']);

function MatchBasis({
  candidateRef,
  basis,
  corrections,
  onToggleCorrection,
}: {
  candidateRef: string;
  basis: MatchBasisItem[];
  corrections: Record<string, boolean>;
  onToggleCorrection: (original: string, on: boolean) => void;
}) {
  const agreements = basis.filter((item) => !NAME_ANCHORS.has(item.anchor));

  return (
    <>
      {agreements.length > 0 ? (
        <ul className="match-basis">
          {agreements.map((item, index) => (
            <li key={`${candidateRef}-${item.anchor}-${index}`}>
              {item.matched || !item.excerpt ? (
                <span className="match-line">
                  {matchesRecordLine(item.matched ?? matchKindWords(item.anchor))}
                </span>
              ) : null}
              {item.excerpt ? <q className="excerpt">{item.excerpt}</q> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {basis.map((item, index) =>
        item.near_match ? (
          <Checkbox
            className="near-match"
            key={`${candidateRef}-near-${index}`}
            checked={corrections[item.near_match.original] ?? true}
            onChange={(checked) => onToggleCorrection(item.near_match?.original ?? '', checked)}
          >
            Matched “{item.near_match.original}”. Treat as {item.near_match.suggested}?
          </Checkbox>
        ) : null,
      )}
    </>
  );
}

export function CandidateCard({
  candidate,
  onConfirm,
  working,
  disabled,
  label = 'Confirm',
  workingLabel = 'Confirming\u2026',
  selection,
  onCorrectionsChange,
}: {
  candidate: Candidate;
  onConfirm: (corrections: Corrections) => void;
  working: boolean;
  disabled: boolean;
  /** "Confirm" on the pick list, "Select this person" on a held row. */
  label?: string;
  workingLabel?: string;
  /** Present in the pick list's same-person mode: a checkbox in place of Confirm. */
  selection?: {
    checked: boolean;
    onChange: (checked: boolean, corrections: Corrections) => void;
  };
  /** For a linked group's own Confirm, which sends every member's answers. */
  onCorrectionsChange?: (corrections: Corrections) => void;
}) {
  const [corrections, setCorrections] = useState<Record<string, boolean>>({});

  const chosen = (answers: Record<string, boolean> = corrections): Corrections =>
    defaultCorrections(candidate).filter((c) => answers[c.original] ?? true);

  const toggleCorrection = (original: string, on: boolean) => {
    const next = { ...corrections, [original]: on };
    setCorrections(next);
    if (selection?.checked) selection.onChange(true, chosen(next));
    onCorrectionsChange?.(chosen(next));
  };

  return (
    <div className="candidate">
      <div className="who">
        <div className="cname">{candidate.name}</div>
        <MatchBasis
          candidateRef={candidate.candidate_ref}
          basis={candidate.match_basis}
          corrections={corrections}
          onToggleCorrection={toggleCorrection}
        />
        {candidate.detail_lines.map((line, index) => (
          <div className="cdetail" key={`${candidate.candidate_ref}-detail-${index}`}>
            {line}
          </div>
        ))}
        {candidate.anti_anchor_collision ? (
          <div className="tag-faint amber-ink block">matched a prior rejection</div>
        ) : null}
      </div>
      {selection ? (
        <Checkbox
          className="same-person"
          checked={selection.checked}
          disabled={disabled}
          onChange={(checked) => selection.onChange(checked, chosen())}
        >
          {selection.checked ? SAME_PERSON_THIS : SAME_PERSON_OTHER}
        </Checkbox>
      ) : (
        <WorkingButton
          label={label}
          workingLabel={workingLabel}
          working={working}
          disabled={disabled}
          onClick={() => onConfirm(chosen())}
        />
      )}
    </div>
  );
}
